import { Styles } from '#~/agent/components/index.js';
import { Cell, Table } from '#~/agent/components/monitoring/layout/index.js';
import { NA } from '#~/agent/components/monitoring/summary/constants.js';
import {
  useAudioDeviceStats,
  useVideoDeviceStats
} from '#~/agent/components/monitoring/summary/device-stats-hook.js';
import { mapResolution } from '#~/agent/components/monitoring/summary/resolution-mapping.js';
import type { Wiring, Wiring$Device } from '#~/agent/components/monitoring/types.js';
import { Tab } from '#~/agent/components/monitoring/types.js';
import { resolutionLookup, useWiring } from '#~/agent/components/monitoring/wiring.js';
import { useAgents } from '#~/agent/hooks.js';
import { Meter } from '#~/audio/components/meter/index.js';
import { codecDisplayName } from '#~/room/components/production/components/codec-warning.js';
import { DataSTag, Sel } from '#~/stag/index.js';
import { useMultiProxyStateQuery } from '#~/statequery/hook.js';
import { Icon } from '@studio/design-system/icon';
import { Spinner } from '@studio/design-system/spinner';
import * as Tabs from '@studio/design-system/tabs';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import type { Agent } from '@studio/models/agent';
import type { Device } from '@studio/models/device';
import { isTransient } from '@studio/models/device';
import type { Room } from '@studio/models/room';
import { isNotNil } from '@tryiris-ai/js-types';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { twMerge } from 'cn';
import { shallow } from 'zustand/shallow';
import { useStore } from '../store/hooks.js';

/** `<MonitoringLite>` component. */
type Props = {
  className?: string;
  agent?: Agent | undefined;
};

/**
 * A simplified version of the DeviceSummaryVideo component
 */
const DeviceSummaryVideoLite = ({
  device,
  roomId
}: {
  device: Wiring$Device;
  roomId: Room['id'];
}): ReactElement => {
  const deviceStatusMap = useMultiProxyStateQuery<{
    found: boolean;
    resolution: string;
    bps: number | undefined;
    framerate: number | undefined;
    network: number | undefined;
    codec: string | undefined;
  }>((state, _proxyID) => {
    const { video } = state?.status?.device?.[device.id] ?? {};

    return {
      found: !!video,
      resolution: mapResolution(video?.status?.width ?? 0, video?.status?.height ?? 0),
      bps: video?.status?.bps,
      framerate: video?.status?.framerate,
      network: video?.status?.network,
      codec: video?.status?.codec
    };
  });

  const downstreamStats = useVideoDeviceStats(roomId, device.id as Device['id']);
  const upstreamStats = Object.values(deviceStatusMap).find((status) => status.found);

  if (!device.video) {
    return <></>;
  }

  // Upstream cells (sending)
  const cells = {
    'Sending': {
      element: 'Sending'
    },
    'Quality Score': {
      element:
        device.connected && isNotNil(upstreamStats?.network)
          ? `${((upstreamStats?.network ?? 0) * 5).toFixed(1)} / 5.0`
          : NA
    },
    'Resolution': { element: upstreamStats?.resolution ?? NA },
    'Bitrate': {
      element:
        device.connected && isNotNil(upstreamStats?.bps)
          ? `${Math.round(upstreamStats?.bps / 1000)} kbps`
          : NA
    },
    'FPS': {
      element:
        device.connected && isNotNil(upstreamStats?.framerate)
          ? `${upstreamStats?.framerate.toFixed(1)}`
          : NA
    },
    // The upstream codec is the one readout a HIL preflight must be able to
    // read per device (is the leased camera still H.265?), so it carries an
    // STag keyed on the device id. Per rule: playwright-stag-selectors-only
    // (ai.knowledge/platform).
    'Codec': {
      element: (
        <span {...DataSTag(Sel.Monitoring, 'upstream_codec', device.id)}>
          {codecDisplayName(upstreamStats?.codec) ?? NA}
        </span>
      )
    }
  };

  // Downstream cells (receiving)
  const downStreamCells = {
    'Receiving': {
      element: 'Receiving'
    },
    'Quality Score': {
      // NOTE: this score is being calculated by the live-downstream-metrics-monitor class
      // Can change this if need be.
      element:
        device.connected && isNotNil(downstreamStats?.quality)
          ? `${(downstreamStats?.quality * 5).toFixed(1)} / 5.0`
          : NA
    },
    'Resolution': {
      element:
        device.connected && isNotNil(downstreamStats?.width) && isNotNil(downstreamStats?.height)
          ? resolutionLookup(downstreamStats?.width, downstreamStats?.height)
          : NA
    },
    'Bitrate': {
      element:
        device.connected && isNotNil(downstreamStats?.bitrate)
          ? `${Math.round(downstreamStats?.bitrate / 1000)} kbps`
          : NA
    },
    'FPS': {
      element:
        device.connected && isNotNil(downstreamStats?.framesPerSecond)
          ? `${downstreamStats?.framesPerSecond.toFixed(1)}`
          : NA
    }
  };

  // These statistics are not currently functional. Uncomment when ready to re-enable.
  // const advancedStatsCells = {
  //   "Advanced Stats": { element: null },
  //   "Dropped Frames": {
  //     element:
  //       device.connected && isNotNil(downstreamStats?.framesDropped)
  //         ? `${downstreamStats.framesDropped.toFixed(0)} / min`
  //         : NA
  //   },
  //   "Packet Loss %": {
  //     element:
  //       device.connected && isNotNil(downstreamStats?.packetLoss)
  //         ? `${downstreamStats.packetLoss.toFixed(2)}%`
  //         : NA
  //   },
  //   "Filler 1": { element: null },
  //   "Filler 2": { element: null }
  // };

  const message = `
  Video/Audio quality uploaded from the Bridge to Iris
  Cloud, influenced by the Bridge's upload bandwidth.
  (This represents the quality once it reaches our Cloud.)
`;

  return (
    <div className='flex grow flex-col'>
      <Table className='grow'>
        <thead>
          <tr className='border-b border-t border-base-50'>
            {Object.entries(cells).map(([label, { element: cellElement }], index) => (
              <Cell
                key={`device_status_${device.id}_${label}`}
                label={label}
                index={index}
                message={message}
                value={cellElement || NA}
                width={`${(100 / Object.keys(cells).length).toFixed(1)}%`}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          <tr>
            {Object.entries(downStreamCells).map(([label, { element: cellElement }], index) => (
              <Cell
                key={`device_downstream_${device.id}_${label}`}
                message={message}
                label={label}
                index={index}
                value={cellElement || NA}
                width={`${(100 / Object.keys(downStreamCells).length).toFixed(1)}%`}
              />
            ))}
          </tr>
          {/* These statistics are not currently functional. Uncomment when ready to re-enable. */}
          {/* {Object.keys(advancedStatsCells).length > 0 && (
            <tr>
              {Object.entries(advancedStatsCells).map(
                ([label, { element: cellElement }], index) => (
                  <Cell
                    key={`device_advanced_${device.id}_${label}`}
                    message={message}
                    label={label}
                    index={index}
                    value={cellElement || null}
                    width={`${(100 / Object.keys(advancedStatsCells).length).toFixed(1)}%`}
                  />
                )
              )}
            </tr>
          )} */}
        </tbody>
      </Table>
    </div>
  );
};

/**
 * A simplified version of the DeviceSummaryAudio component
 */
const DeviceSummaryAudioLite = ({
  device,
  roomId
}: {
  device: Wiring$Device;
  roomId: Room['id'];
}): ReactElement => {
  type CellType = {
    element: string | number | ReactElement;
    containerClassName?: string;
  };

  const deviceStatusMap = useMultiProxyStateQuery<{
    found: boolean;
    bps: number | undefined;
    codec: string | undefined;
  }>((state, _proxyID) => {
    const { audio } = state?.status?.device?.[device.id] ?? {};

    return {
      found: !!audio,
      bps: audio?.status?.bps,
      codec: audio?.status?.codec
    };
  });

  const upstreamStats = Object.values(deviceStatusMap).find((status) => status.found);

  // Upstream cells (sending)
  const cells: Record<string, CellType> = {
    'Sending': {
      element: 'Sending'
    },
    'Source': {
      element: device.name || NA
    },
    'Audio Level': {
      element: <Meter deviceId={device.id as Device['id']} />,
      containerClassName: 'w-[100%]'
    },
    'Bitrate': {
      element:
        device.connected && isNotNil(upstreamStats?.bps)
          ? `${Math.round((upstreamStats.bps ?? 0) / 1000)} kbps`
          : NA
    },
    'Codec': {
      element: upstreamStats?.codec ?? NA
    }
  };

  const downstreamStats = useAudioDeviceStats(roomId, device.id as Device['id']);

  // Downstream cells (receiving)
  const downStreamCells: Record<string, CellType> = {
    'Receiving': {
      element: 'Receiving'
    },
    'Audio Level': {
      element: <Meter deviceId={device.id as Device['id']} />,
      containerClassName: 'w-[100%]'
    },
    'Bitrate': {
      element:
        device.connected && isNotNil(downstreamStats?.bitrate)
          ? `${Math.round(downstreamStats.bitrate / 1000)} kbps`
          : NA
    },
    'Codec': {
      element: downstreamStats?.codec ?? NA
    }
  };

  const message = `
  Video/Audio quality uploaded from the Bridge to Iris
  Cloud, influenced by the Bridge's upload bandwidth.
  (This represents the quality once it reaches our Cloud.)
`;

  return (
    <div className='flex grow flex-col'>
      <Table className='grow'>
        <thead>
          <tr className='border-b border-t border-base-50'>
            {Object.entries(cells).map(
              ([label, { element: cellElement, containerClassName }], index) => (
                <Cell
                  key={`device_status_${device.id}_${label}`}
                  message={message}
                  containerClassName={containerClassName}
                  label={label}
                  index={index}
                  value={cellElement}
                  width={`${(100 / Object.keys(cells).length).toFixed(1)}%`}
                />
              )
            )}
          </tr>
        </thead>
        <tbody>
          <tr>
            {Object.entries(downStreamCells).map(
              ([label, { element: cellElement, containerClassName }], index) => (
                <Cell
                  key={`device_downstream_${device.id}_${label}`}
                  message={message}
                  containerClassName={containerClassName}
                  label={label}
                  index={index}
                  value={cellElement}
                  width={`${(100 / Object.keys(downStreamCells).length).toFixed(1)}%`}
                />
              )
            )}
          </tr>
        </tbody>
      </Table>
    </div>
  );
};

/** A simplified monitoring summary for one room. */
export const SummaryLite = ({
  roomId,
  wiring
}: {
  roomId: Room['id'];
  wiring: Wiring;
}): ReactElement => {
  const { summary } = wiring;

  const videoCount = summary.videoDevices?.length ?? 0;
  const audioCount = summary.audioDevices?.length ?? 0;

  const showOverview = videoCount === 0 && audioCount === 0;

  const [tab, setTab] = useState<Tab>(showOverview ? Tab.Overview : Tab.Video);

  return (
    <Tabs.Root
      className='h-auto grow overflow-visible'
      onChange={(tab): void => {
        setTab(tab as Tab);
      }}
      value={tab as string}
    >
      {/* List of Tab types: Video, Audio */}
      <Tabs.List className='mb-40 border-base-50 px-0'>
        {showOverview && <Tabs.Trigger value={Tab.Overview}>Overview</Tabs.Trigger>}
        <Tabs.Trigger disabled={videoCount === 0} value={Tab.Video}>
          Video Sources {videoCount > 0 && `(${videoCount})`}
        </Tabs.Trigger>
        <Tabs.Trigger disabled={audioCount === 0} value={Tab.Audio}>
          Audio Sources {audioCount > 0 && `(${audioCount})`}
        </Tabs.Trigger>
      </Tabs.List>

      {/* Overview Tab Content */}
      <Tabs.Content className='flex flex-col gap-16 overflow-y-auto' value={Tab.Overview}>
        <div className='p-16 text-center text-muted'>No monitoring data available</div>
      </Tabs.Content>

      {/* Video Tab Content */}
      <Tabs.Content className='flex flex-col gap-16 overflow-y-auto' value={Tab.Video}>
        {summary.videoDevices
          ?.map(
            (device: Wiring$Device) =>
              !isTransient(device.id as Device['id']) && (
                <DeviceSummaryVideoLite
                  device={device}
                  key={`device_summary_${device.id}`}
                  roomId={roomId}
                />
              )
          )
          .filter((element: React.ReactElement | false | undefined) => !!element)}
      </Tabs.Content>

      {/* Audio Tab Content */}
      <Tabs.Content className='flex flex-col gap-16 overflow-y-auto' value={Tab.Audio}>
        {summary.audioDevices?.map((device: Wiring$Device) => (
          <DeviceSummaryAudioLite
            device={device}
            key={`device_summary_${device.id}`}
            roomId={roomId}
          />
        ))}
      </Tabs.Content>
    </Tabs.Root>
  );
};

export const MonitoringLite = (props: Props): ReactElement => {
  const { agent, className } = props;
  const [candidate] = useStore((µ) => [µ.candidate], shallow);

  const agentIds = useAgents((µ) => µ.map((a) => a.id), shallow);
  const roomId = agent?.roomId;
  const hasMonitoringContext = agentIds.length > 0 && !!roomId;

  const wiring = useWiring(hasMonitoringContext ? agentIds : [], roomId);

  if (!hasMonitoringContext) {
    return <Spinner className='text-primary' />;
  }

  const agents = Object.values(wiring.agents);
  const allOffline = agents.length > 0 && agents.every((agent) => agent.status === 'offline');
  const style = Styles.status[agents[0]?.status ?? 'offline'];

  if (agents.length === 0 || !agents[0]?.id) {
    return (
      <div className={twMerge('w-full', className)}>
        <div className={twMerge('flex flex-col gap-16 overflow-y-auto p-16')}>
          <div className='p-16 text-center text-muted'>No monitoring data available</div>
        </div>
      </div>
    );
  }

  const filteredWiring = {
    ...wiring,
    summary: {
      ...wiring.summary,
      videoDevices: wiring.summary.videoDevices?.filter((device) => device.id === candidate.id),
      audioDevices: wiring.summary.audioDevices?.filter((device) => device.id === candidate.id)
    }
  };

  const isPtzoptics = vendorName().toLowerCase() === 'hive';

  return (
    <div
      className={twMerge('rounded-4 h-full p-16', isPtzoptics ? 'bg-base-100' : 'bg-[#22293E]')}
      data-theme={isPtzoptics ? 'ptz-optics' : 'dark'}
    >
      {/* Merge resolution: main's single-column layout (ENG-3262 removed the
          Columns/Preview wrapper) + this branch's roomId threading (ENG-3458). */}
      <SummaryLite roomId={roomId} wiring={filteredWiring} />
      {!!allOffline && (
        <div className={twMerge('flex items-center gap-8 rounded border p-6', style.border)}>
          <div>
            <Icon className={twMerge('text-xl', style.text)} icon='alert-triangle' />
          </div>

          <p className='grow'>
            {vendorName()} is unable to connect to your agent. Please check your internet connection
            or restart the {vendorName()} Bridge.
          </p>
        </div>
      )}
    </div>
  );
};
